import { useEffect } from "react";
import type { Project } from "../types/Project";

type UseKeyboardNavigationProps = {
  projects: Project[];
  focusedIndex: number;
  setFocusedIndex: (index: number | ((prev: number) => number)) => void;
  activeProject: Project | null;
  imagePreview: string | null;
  openProject: (project: Project) => void;
  closeProject: () => void;
  setImagePreview: (preview: string | null) => void;
};

export function useKeyboardNavigation({
  projects,
  focusedIndex,
  setFocusedIndex,
  activeProject,
  imagePreview,
  openProject,
  closeProject,
  setImagePreview,
}: UseKeyboardNavigationProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (imagePreview) {
        if (e.key === "Escape") {
          setImagePreview(null);
        }
        return;
      }

      if (activeProject) {
        if (e.key === "Escape") {
          closeProject();
        }
        return;
      }

      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((i) => Math.min(i + 1, projects.length - 1));
          break;
        case "ArrowLeft":
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((i) => Math.max(i - 1, 0));
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (projects[focusedIndex]) {
            openProject(projects[focusedIndex]);
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeProject, focusedIndex, openProject, closeProject, imagePreview, projects, setFocusedIndex, setImagePreview]);
}
